import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Filter, Trash2, Plus, EyeOff, RotateCcw } from "lucide-react";

export default function ExcludedIpsSection() {
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeIps, setPurgeIps] = useState([]);

  const load = () =>
    api.get("/settings/excluded-ips").then(({ data }) => setIps(data.ips || []));

  useEffect(() => {
    load();
  }, []);

  const save = async (next) => {
    setLoading(true);
    try {
      const { data } = await api.put("/settings/excluded-ips", { ips: next });
      setIps(data.ips);
      return true;
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const add = async () => {
    const v = newIp.trim();
    if (!v) return;
    if (ips.includes(v)) {
      toast.info("IP già presente");
      setNewIp("");
      return;
    }
    const ok = await save([...ips, v]);
    if (ok) {
      toast.success("IP aggiunto");
      setNewIp("");
    }
  };

  const removeAt = async (idx) => {
    const next = ips.filter((_, i) => i !== idx);
    const ok = await save(next);
    if (ok) toast.success("IP rimosso");
  };

  const openPurge = () => {
    // Only exact IPs (not CIDR) supported for purging
    const exact = ips.filter((i) => !i.includes("/"));
    if (!exact.length) {
      toast.info("Aggiungi prima almeno un IP esatto");
      return;
    }
    setPurgeIps(exact);
    setPurgeOpen(true);
  };

  const runPurge = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/settings/purge-hits", { ips: purgeIps });
      toast.success(
        `${data.deleted} hit rimossi · ${data.recomputed_campaigns} campagne ricalcolate`
      );
      setPurgeOpen(false);
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border bg-white p-6" data-testid="excluded-ips-section">
      <div className="flex items-center gap-2 mb-2">
        <Filter className="w-5 h-5 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-xl">IP esclusi dalle statistiche</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Gli IP qui elencati non genereranno aperture né click. Utile per escludere il tuo IP ufficio,
        VPN aziendale, o server di test. Puoi inserire un IP esatto (es. <code className="bg-muted px-1 text-xs">178.175.237.142</code>)
        o una subnet CIDR (es. <code className="bg-muted px-1 text-xs">192.168.1.0/24</code>).
      </p>

      <div className="flex gap-2 mb-4">
        <Input
          value={newIp}
          onChange={(e) => setNewIp(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="1.2.3.4 oppure 192.168.1.0/24"
          data-testid="new-ip-input"
        />
        <Button onClick={add} disabled={loading || !newIp.trim()} data-testid="add-ip-btn">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi
        </Button>
      </div>

      {ips.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center rounded-sm">
          Nessun IP escluso. Tutti gli eventi di apertura/click verranno registrati.
        </div>
      ) : (
        <div className="border border-border">
          {ips.map((ip, i) => (
            <div
              key={ip}
              className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0"
              data-testid={`excluded-ip-${ip}`}
            >
              <div className="flex items-center gap-3">
                <EyeOff className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <code className="text-sm font-mono">{ip}</code>
                {ip.includes("/") && (
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    subnet
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeAt(i)}
                disabled={loading}
                data-testid={`remove-ip-${ip}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {ips.filter((i) => !i.includes("/")).length > 0 && (
        <div className="mt-4">
          <Button
            variant="outline"
            onClick={openPurge}
            data-testid="purge-ips-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Rimuovi eventi già registrati per questi IP
          </Button>
        </div>
      )}

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle>Ricalcola statistiche</DialogTitle>
            <DialogDescription>
              Verranno rimossi TUTTI gli eventi di tracking associati agli IP esatti qui sotto,
              e i contatori delle campagne verranno ricalcolati. Le subnet CIDR non sono supportate
              per la pulizia retroattiva.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {purgeIps.map((ip) => (
              <div
                key={ip}
                className="font-mono text-sm bg-muted/40 border border-border px-3 py-2"
              >
                {ip}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={runPurge}
              disabled={loading}
              data-testid="purge-confirm"
            >
              Conferma pulizia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
